// Re-exports
export { StorageTagger } from "./tagger";
export type { StorageTaggerConfig, TagUploadParams } from "./tagger";
export { MdcUploader, createMdcUploader } from "./mdc-uploader";
export type { MdcUploaderConfig, AttachmentInput, MdcUploadParams, MdcUploadResult } from "./mdc-uploader";

/**
 * Client-side storage upload primitive.
 *
 * Performs the prepare → PUT → complete cycle against the PineSeed storage API.
 * Zero knowledge of domain logic — just uploads bytes and returns the result.
 * All config injected at the boundary.
 */

/** Config injected at the boundary. */
export interface StorageUploaderConfig {
  /** Base API URL (e.g. "https://api.example.com") */
  apiUrl: string;
  /** Auth headers sent on all API requests (e.g. { "X-API-Key": key }) */
  authHeaders: Record<string, string>;
  /** Additional fetch options merged into API requests (e.g. { credentials: "include" }) */
  fetchOptions?: RequestInit;
  /** Transport for all requests (API calls and presigned-URL PUT); the caller decides routing. */
  fetchApi: typeof fetch;
  /** Signed URL TTL in seconds */
  uploadTtlS: number;
}

/** Sub-scope fields merged into prepare/complete requests. All optional, as in the API's request schema. */
export interface UploadSubScope {
  /** 'account': the account level (tenant only); absent or 'workspace': the credential's workspace. */
  scopeLevel?: "account" | "workspace";
  conversation?: string;
  thread?: string;
  message?: string;
  participant?: string;
}

/** Response from upload-prepare endpoint. */
interface UploadPrepareResponse {
  uploadUrl: string;
  filename: string;
  timestamp: number;
  expiresAt: number;
}

/** Result returned from a successful upload. */
export interface UploadResult {
  filename: string;
  timestamp: number;
}

export class StorageUploader {
  readonly #config: StorageUploaderConfig;
  readonly #prepareUrl: string;
  readonly #completeUrl: string;

  constructor(config: StorageUploaderConfig) {
    this.#config = config;
    this.#prepareUrl = `${config.apiUrl}/api/storage/upload-prepare`;
    this.#completeUrl = `${config.apiUrl}/api/storage/upload-complete`;
  }

  async upload(
    data: Uint8Array | Buffer | ReadableStream<Uint8Array>,
    contentType: string,
    filename: string,
    bucketKey: string,
    subScope: UploadSubScope,
  ): Promise<UploadResult> {
    const { authHeaders, fetchOptions, fetchApi, uploadTtlS } = this.#config;

    // 1. Prepare — get signed URL
    const prepareRes = await fetchApi(this.#prepareUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      ...fetchOptions,
      body: JSON.stringify({
        bucketKey,
        filename,
        contentType,
        ttl: uploadTtlS,
        ...subScope,
      }),
    });
    if (!prepareRes.ok) {
      const body = await prepareRes.text().catch(() => prepareRes.statusText);
      throw new Error(
        `StorageUploader: prepare failed status=${prepareRes.status}: ${body}`,
      );
    }
    const {
      uploadUrl,
      filename: returnedFilename,
      timestamp,
    } = (await prepareRes.json()) as UploadPrepareResponse;

    // 2. PUT to signed URL
    const putRes = await fetchApi(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body:
        data instanceof ReadableStream
          ? data
          : new Blob([new Uint8Array(data)], { type: contentType }),
    });
    if (!putRes.ok) {
      const body = await putRes.text().catch(() => putRes.statusText);
      throw new Error(
        `StorageUploader: PUT failed filename=${returnedFilename} status=${putRes.status}: ${body}`,
      );
    }

    // 3. Complete — write latest pointer
    const completeRes = await fetchApi(this.#completeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      ...fetchOptions,
      body: JSON.stringify({
        bucketKey,
        filename: returnedFilename,
        timestamp,
        ...subScope,
      }),
    });
    if (!completeRes.ok) {
      const body = await completeRes.text().catch(() => completeRes.statusText);
      throw new Error(
        `StorageUploader: complete failed filename=${returnedFilename} status=${completeRes.status}: ${body}`,
      );
    }

    return { filename: returnedFilename, timestamp };
  }
}
